import React, { useState, useEffect, useRef, useCallback } from 'react'
import Badge from '@material-ui/core/Badge'
import BugReportIcon from '@material-ui/icons/BugReport'
import useCommand from 'components/useCommand'
import { useWebsocketEvent } from 'modules/websocket'
import useAddFlashMessage from 'reacticoon-plugin-flash-messages/useAddFlashMessage'
import SecurityManager from 'modules/security/SecurityManager'
import { isBugtrackerManager } from './access'

/**
 * Notifications & non-lu du bugtracker.
 *
 * Règle : une notification en haut à droite UNIQUEMENT pour les tickets où l'on PARTICIPE
 * (créé ou déjà répondu) — jamais pour l'activité des autres. Les événements websocket
 * embarquent `participantIdentifiers` pour trancher côté client.
 *
 * ⚠️ EXCEPTION : les responsables (api.bugtracker.canManage) suivent TOUT le bugtracker —
 * chaque nouveau ticket et chaque réponse, même là où ils ne sont jamais intervenus. Ce
 * sont eux qui trient, ils ne peuvent pas attendre d'être cités pour être au courant.
 *
 * Le badge (nombre de tickets non lus) vit sur le bouton de la sidebar : le hook est
 * monté par l'icône de ce bouton, donc actif sur tout le site tant qu'on a accès.
 *
 * `READ_CHANGED_EVENT` : événement DOM local émis quand on vient de LIRE un ticket
 * (ouverture du détail, activité reçue pendant qu'il est ouvert). C'est le pont minimal
 * entre la page et le badge — pas de plomberie redux pour un simple compteur.
 */

export const READ_CHANGED_EVENT = 'bugtrackerReadChanged'

export const notifyBugtrackerReadChanged = () => {
  window.dispatchEvent(new CustomEvent(READ_CHANGED_EVENT))
}

const truncateTitle = (title) => {
  const text = String(title || '')
  return text.length > 60 ? `${text.slice(0, 57)}…` : text
}

const STATUS_TOASTS = {
  RESOLVED: (title) => `✅ « ${title} » est prévu pour la prochaine mise à jour`,
  CLOSED: (title) => `🔒 « ${title} » a été fermé`,
  OPEN: (title) => `🔓 « ${title} » a été rouvert`,
  ARCHIVED: (title) => `📦 « ${title} » a été archivé`,
}

export function useBugtrackerNotifications() {
  const { addInfoFlashMessage } = useAddFlashMessage()
  const [unreadCount, setUnreadCount] = useState(0)

  const { runCommand: fetchCount } = useCommand({
    command: 'BUGTRACKER_GET_UNREAD_COUNT',
    manualRun: true,
    id: 'badge',
    onSuccess: (data) => setUnreadCount(data?.total || 0),
    // Silencieux : un badge n'a pas le droit de faire râler la page.
    onFailure: () => {},
  })
  const fetchCountRef = useRef(fetchCount)
  fetchCountRef.current = fetchCount

  // Regroupe les rafales d'événements en une seule requête de compteur.
  const timerRef = useRef(null)
  const scheduleRefresh = useCallback(() => {
    window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => fetchCountRef.current({}), 800)
  }, [])

  useEffect(() => {
    fetchCountRef.current({})
    const onReadChanged = () => scheduleRefresh()
    window.addEventListener(READ_CHANGED_EVENT, onReadChanged)
    return () => {
      window.removeEventListener(READ_CHANGED_EVENT, onReadChanged)
      window.clearTimeout(timerRef.current)
    }
  }, [scheduleRefresh])

  const handleTicketUpdated = useCallback((data) => {
    if (!data || !data.ticketId) {
      return
    }
    // Les votes ne touchent ni l'activité ni le non-lu : rien à faire.
    if (data.kind === 'vote') {
      return
    }
    scheduleRefresh()

    const myIdentifier = SecurityManager.player && SecurityManager.player.identifier
    const participants = data.participantIdentifiers || []
    // Un responsable est notifié de tout ; les autres, seulement là où ils participent.
    if (!isBugtrackerManager() && (!myIdentifier || !participants.includes(myIdentifier))) {
      return
    }

    if (data.kind === 'message' && data.message && data.message.authorIdentifier !== myIdentifier) {
      addInfoFlashMessage({
        text: `💬 ${data.message.authorName} a répondu sur « ${truncateTitle(data.ticketTitle)} »`,
      })
    }
    if (data.kind === 'status' && data.actorIdentifier !== myIdentifier && STATUS_TOASTS[data.status]) {
      addInfoFlashMessage({ text: STATUS_TOASTS[data.status](truncateTitle(data.ticketTitle)) })
    }
    if (data.kind === 'edited' && data.actorIdentifier !== myIdentifier) {
      addInfoFlashMessage({ text: `✏️ « ${truncateTitle(data.ticketTitle)} » a été modifié` })
    }
    // kind === 'priority' : le compteur bouge, mais pas de toast — trop de bruit.
  }, [addInfoFlashMessage, scheduleRefresh])

  useWebsocketEvent('bugtrackerTicketUpdated', { onMessage: handleTicketUpdated })

  /**
   * Nouveaux tickets : seuls les responsables sont notifiés — personne d'autre ne
   * « participe » encore à un ticket qui vient de naître. Le compteur, lui, se
   * rafraîchit pour eux aussi (il ne bougera que s'ils suivent le ticket).
   */
  const handleTicketCreated = useCallback((data) => {
    scheduleRefresh()
    const myIdentifier = SecurityManager.player && SecurityManager.player.identifier
    if (!isBugtrackerManager() || (myIdentifier && data?.ticket?.creatorIdentifier === myIdentifier)) {
      return
    }
    const type = data?.ticket?.type === 'BUG' ? '🐛 Nouveau bug' : '💡 Nouvelle suggestion'
    addInfoFlashMessage({ text: `${type} : « ${truncateTitle(data?.ticket?.title)} »` })
  }, [addInfoFlashMessage, scheduleRefresh])

  useWebsocketEvent('bugtrackerTicketCreated', { onMessage: handleTicketCreated })

  return { unreadCount }
}

/**
 * Icône du bouton sidebar : le badge de non-lus, et — parce qu'elle est montée partout —
 * le point d'ancrage des notifications ciblées.
 */
export const BugtrackerSidebarIcon = () => {
  const { unreadCount } = useBugtrackerNotifications()
  return (
    <Badge badgeContent={unreadCount} color="error" max={9} overlap="rectangular">
      <BugReportIcon />
    </Badge>
  )
}
